import { useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarRange, Loader2, Mail, RefreshCw, Send } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk } from "@/lib/queryClient";
import { toast } from "sonner";


const SEGMENTS = [
  { slug: "global",       label: "GLOBAL WEEKLY NEWS HEADLINES" },
  { slug: "americas",     label: "AMERICAS WEEKLY NEWS HEADLINES" },
  { slug: "asia_pacific", label: "ASIA-PACIFIC WEEKLY NEWS HEADLINES" },
  { slug: "europe",       label: "EUROPE WEEKLY NEWS HEADLINES" },
  { slug: "mea",          label: "MIDDLE EAST & AFRICA WEEKLY NEWS HEADLINES" },
  { slug: "oceania",      label: "OCEANIA WEEKLY NEWS HEADLINES" },
  { slug: "technology",   label: "TECHNOLOGY WEEKLY NEWS HEADLINES" },
];


function StatusPill({ status }) {
  const map = {
    review: { bg: "#EFF6FF", fg: "#1D4ED8", label: "Review" },
    sent:   { bg: "#ECFDF5", fg: "#047857", label: "Sent" },
  };
  const m = map[status] || { bg: "#F1F5F9", fg: "#475569", label: status };
  return (
    <span
      className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ background: m.bg, color: m.fg }}
    >{m.label}</span>
  );
}


function DeliveryLine({ latest }) {
  if (!latest) {
    return (
      <div className="mono text-[11px] text-hm-fg-subtle" data-testid="weekly-delivery-status">
        Not delivered yet.
      </div>
    );
  }
  const when = new Date(latest.sent_at).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const via = latest.trigger === "auto_cron" ? "automated" : latest.trigger;
  return (
    <div className="mono text-[11px] text-hm-fg-muted" data-testid="weekly-delivery-status">
      Delivered to <span className="text-hm-fg">{(latest.to_emails || []).join(", ")}</span>
      {" · "}{when}{" · "}<span className="text-hm-fg">{via}</span>
    </div>
  );
}


function SegmentBlock({ seg, items, weekLabel }) {
  const label = SEGMENTS.find(s => s.slug === seg)?.label || seg.toUpperCase();
  return (
    <div className="hm-surface p-6 mb-6" data-testid={`weekly-segment-${seg}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-10 rounded" style={{ background: "#4FA8E0" }} />
        <div>
          <div className="mono text-[11px] font-bold uppercase tracking-widest" style={{ color: "#4FA8E0" }}>
            {label}
          </div>
          <div className="mono text-[11px] text-hm-fg-subtle mt-1">
            {items.length} headline{items.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-hm-fg-muted leading-relaxed">
          <strong className="text-hm-fg">
            BIGGEST STORIES OF THE PAST WEEK ({(SEGMENTS.find(s => s.slug === seg)?.label || "").replace(" WEEKLY NEWS HEADLINES", "")}, {weekLabel}).
          </strong>{" "}
          No material stories this week.
        </p>
      ) : (
        <p className="text-sm text-hm-fg leading-relaxed" style={{ lineHeight: 1.75 }}>
          <strong>
            BIGGEST STORIES OF THE PAST WEEK ({(SEGMENTS.find(s => s.slug === seg)?.label || "").replace(" WEEKLY NEWS HEADLINES", "")}, {weekLabel}).
          </strong>{" "}
          {items.map((it, i) => (
            <span key={i}>
              <span style={{ color: "#64748B" }}>({i + 1}) </span>
              {it.url ? (
                <a
                  href={it.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                  style={{ color: "#4FA8E0", textDecoration: "none" }}
                  data-testid={`weekly-headline-link-${seg}-${i + 1}`}
                >
                  {it.headline_condensed || it.title}
                </a>
              ) : (
                <span>{it.headline_condensed || it.title}</span>
              )}
              .{" "}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}


export default function WeeklyBriefDetail() {
  const { id } = useParams();
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const queryClient = useQueryClient();

  /*
   * Turn CC · 16 — Migrated to react-query. `brief` and `sends` are
   * fetched via useQuery so the page paints instantly from the persist
   * cache on subsequent visits, then silently revalidates. `sendNow`
   * stays as a straight fetch (one-shot mutation) but invalidates the
   * two query keys on success to refresh the delivery status without
   * a full page reload.
   */
  const briefQ = useQuery({
    queryKey: qk.weeklyBriefDetail(id),
    queryFn: () => fetchers.weeklyBriefDetail(id),
    staleTime: 5 * 60_000,
    enabled: !!id,
  });
  const sendsQ = useQuery({
    queryKey: qk.weeklyBriefSendHistory(id),
    queryFn: () => fetchers.weeklyBriefSendHistory(id),
    staleTime: 60_000,
    refetchInterval: 30_000,
    enabled: !!id,
  });

  const brief = briefQ.data;
  const sends = sendsQ.data?.sends || [];
  const error = briefQ.error;

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API}/weekly-brief-v2/${id}/send-email`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ to: ["lenka.galinie@gmail.com"] }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      toast.success(`Sent · ${body.gmail_message_id}`);
      queryClient.invalidateQueries({ queryKey: qk.weeklyBriefSendHistory(id) });
      queryClient.invalidateQueries({ queryKey: qk.weeklyBriefDetail(id) });
    } catch (e) {
      toast.error(`Send failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  if (error && !brief) {
    return <div className="hm-surface p-6 text-hm-danger">{error.message || String(error)}</div>;
  }
  if (!brief) {
    return <div className="hm-surface p-6 mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">Loading…</div>;
  }

  const payload = brief.payload_v2 || {};
  const segments = payload.segments || {};
  const weekLabel = brief.week_label || payload.week_label;
  const latestOk = sends.find(s => s.status === "sent");

  return (
    <div data-testid="weekly-brief-detail-page">
      <PageHeader
        title={`Weekly Brief · ${weekLabel}`}
        subtitle={`${brief.week_start} → ${brief.week_end}`}
      />

      <div className="mb-4"><DeliveryLine latest={latestOk} /></div>

      <div className="hm-surface p-6 mb-6" data-testid="weekly-hero">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-hm-fg">{weekLabel}</h1>
            <div className="mono text-[11px] text-hm-fg-subtle mt-2">
              {brief.article_count} headlines · 7 sections · {brief.pipeline_version}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={brief.status} />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowPreview(true)}
            data-testid="weekly-preview-email"
            className="h-9 px-4 rounded-md text-xs border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong inline-flex items-center gap-1.5"
          >
            <Mail className="h-3.5 w-3.5" />
            Preview email
          </button>
          <button
            onClick={sendNow}
            disabled={sending}
            data-testid="weekly-send-now"
            className="h-9 px-4 rounded-md text-xs bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send now
          </button>
          <button
            onClick={() => briefQ.refetch()}
            data-testid="weekly-refresh"
            className="h-9 px-3 rounded-md text-xs text-hm-fg-muted hover:text-hm-fg inline-flex items-center gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
      </div>

      <div>
        {SEGMENTS.map(s => (
          <SegmentBlock
            key={s.slug}
            seg={s.slug}
            items={segments[s.slug] || []}
            weekLabel={weekLabel}
          />
        ))}
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="weekly-email-preview-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-hm-border shrink-0">
              <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">Email preview</div>
              <button
                onClick={() => setShowPreview(false)}
                data-testid="weekly-email-preview-close"
                className="text-hm-fg-muted hover:text-hm-fg text-sm"
              >Close</button>
            </div>
            <iframe
              title="weekly-preview"
              src={`${API}/weekly-brief-v2/${id}/preview-email`}
              className="flex-1"
              style={{ border: 0 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
